import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Calendar as CalendarIcon,
  Check,
  Loader2,
  MapPin,
  Repeat,
  Trash2,
  Users,
  X,
  ExternalLink,
  Save,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import type { CalendarEventDto, CalendarRsvpResponse } from '@cc/types';

type RecurrenceScope = 'this' | 'following' | 'all';

interface Props {
  event: CalendarEventDto | null;
  onClose: () => void;
}

/**
 * View + edit + delete + RSVP for a single event. Opens as a centered dialog;
 * mobile is handled by Radix's responsive sheet behaviour.
 */
export function EventDetailSheet({ event, onClose }: Props) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [summary, setSummary] = useState('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isAllDay, setIsAllDay] = useState(false);
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [attendeesInput, setAttendeesInput] = useState('');
  const [notifyAttendees, setNotifyAttendees] = useState(true);
  // Recurrence-scope decision flow: when Karl hits Save/Delete on an event
  // that's part of a recurring series, we open a small dialog asking whether
  // to apply to this instance only, this and following, or all. `pending` is
  // the action to fire once a scope is picked.
  const [pending, setPending] = useState<null | 'save' | 'delete'>(null);
  const isRecurring = Boolean(event?.recurringEventId);

  useEffect(() => {
    if (!event) return;
    setEditing(false);
    setSummary(event.summary);
    const start = new Date(event.startTime);
    const end = new Date(event.endTime);
    setDate(format(start, 'yyyy-MM-dd'));
    setStartTime(format(start, 'HH:mm'));
    setEndTime(format(end, 'HH:mm'));
    setIsAllDay(event.isAllDay);
    setLocation(event.location ?? '');
    setDescription(event.description ?? '');
    setAttendeesInput(event.attendees.join(', '));
  }, [event]);

  const updateMutation = useMutation({
    mutationFn: (scope: RecurrenceScope) => {
      if (!event) throw new Error('no event');
      const startIso = composeIso(date, startTime, isAllDay);
      const endIso = composeIso(date, endTime, isAllDay);
      const attendees = parseAttendees(attendeesInput);
      return api.updateCalendarEvent(event.id, {
        summary: summary.trim(),
        description: description.trim() || null,
        location: location.trim() || null,
        startIso,
        endIso,
        isAllDay,
        attendees,
        notifyAttendees,
        recurrenceScope: isRecurring ? scope : undefined,
      });
    },
    onSuccess: () => {
      toast.success('Event saved.');
      void queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      setPending(null);
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : 'Could not save event.';
      toast.error(msg);
      setPending(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (scope: RecurrenceScope) => {
      if (!event) throw new Error('no event');
      return api.deleteCalendarEvent(event.id, notifyAttendees, isRecurring ? scope : 'this');
    },
    onSuccess: () => {
      toast.success('Event deleted.');
      void queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
      setPending(null);
      onClose();
    },
    onError: () => {
      toast.error('Could not delete event.');
      setPending(null);
    },
  });

  function fireSave() {
    if (isRecurring) {
      setPending('save');
    } else {
      updateMutation.mutate('this');
    }
  }
  function fireDelete() {
    if (isRecurring) {
      setPending('delete');
    } else {
      deleteMutation.mutate('this');
    }
  }
  function pickScope(scope: RecurrenceScope) {
    if (pending === 'save') updateMutation.mutate(scope);
    else if (pending === 'delete') deleteMutation.mutate(scope);
  }

  const rsvpMutation = useMutation({
    mutationFn: (response: CalendarRsvpResponse) => {
      if (!event) throw new Error('no event');
      return api.rsvpCalendarEvent(event.id, response);
    },
    onSuccess: (_d, response) => {
      toast.success(`RSVP: ${response}`);
      void queryClient.invalidateQueries({ queryKey: ['calendar-events'] });
    },
    onError: () => toast.error('Could not RSVP.'),
  });

  if (!event) return null;

  const start = new Date(event.startTime);
  const end = new Date(event.endTime);

  return (
    <Dialog open={Boolean(event)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {editing ? (
              <Input
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                className="text-base font-semibold"
              />
            ) : (
              <span>{event.summary || '(no title)'}</span>
            )}
          </DialogTitle>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <CalendarIcon className="h-3 w-3" />
            {event.isAllDay
              ? `${format(start, 'EEE d MMM')} (all day)`
              : `${format(start, 'EEE d MMM, h:mm a')} – ${format(end, 'h:mm a')}`}
            <Badge variant="outline" className="ml-1 text-[10px]">
              {event.accountEmail}
            </Badge>
            {event.projectName && (
              <Badge variant="outline" className="text-[10px]">
                {event.projectName}
              </Badge>
            )}
            {isRecurring && (
              <Badge variant="outline" className="text-[10px]">
                <Repeat className="mr-1 h-2.5 w-2.5" /> Recurring
              </Badge>
            )}
          </div>
        </DialogHeader>

        {editing ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <input
                id="ed-isAllDay"
                type="checkbox"
                checked={isAllDay}
                onChange={(e) => setIsAllDay(e.target.checked)}
              />
              <label htmlFor="ed-isAllDay">All day</label>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Date</Label>
                <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              {!isAllDay && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Start</Label>
                    <Input
                      type="time"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>End</Label>
                    <Input
                      type="time"
                      value={endTime}
                      onChange={(e) => setEndTime(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
            <div>
              <Label>Location</Label>
              <Input value={location} onChange={(e) => setLocation(e.target.value)} />
            </div>
            <div>
              <Label>Attendees</Label>
              <Input
                value={attendeesInput}
                onChange={(e) => setAttendeesInput(e.target.value)}
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <input
                id="ed-notify"
                type="checkbox"
                checked={notifyAttendees}
                onChange={(e) => setNotifyAttendees(e.target.checked)}
              />
              <label htmlFor="ed-notify">Email attendees about this change</label>
            </div>
          </div>
        ) : (
          <div className="space-y-2 text-sm">
            {event.source === 'AI_COMMITMENT' && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1.5 text-[11px] text-amber-200">
                <p className="font-medium">Auto-extracted from your sent email</p>
                <p className="mt-0.5 whitespace-pre-wrap text-amber-300/80">
                  {event.description ?? ''}
                </p>
                {event.confidence != null && (
                  <p className="mt-1 text-amber-300/60">
                    Confidence: {Math.round(event.confidence * 100)}%
                  </p>
                )}
              </div>
            )}
            {event.location && (
              <p className="flex items-center gap-2 text-muted-foreground">
                <MapPin className="h-3.5 w-3.5" /> {event.location}
              </p>
            )}
            {event.attendees.length > 0 && (
              <div>
                <p className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                  <Users className="h-3 w-3" /> Attendees
                </p>
                <p className="mt-1 text-sm">{event.attendees.join(', ')}</p>
              </div>
            )}
            {event.description && (
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Description</p>
                <p className="mt-1 whitespace-pre-wrap text-sm">{event.description}</p>
              </div>
            )}
            {event.organizerEmail && event.organizerEmail !== event.accountEmail && (
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground">Organiser</p>
                <p className="mt-1 text-sm">{event.organizerEmail}</p>
                <div className="mt-2 flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={rsvpMutation.isPending}
                    onClick={() => rsvpMutation.mutate('accepted')}
                  >
                    <Check className="h-3 w-3" /> Accept
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={rsvpMutation.isPending}
                    onClick={() => rsvpMutation.mutate('tentative')}
                  >
                    Maybe
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={rsvpMutation.isPending}
                    onClick={() => rsvpMutation.mutate('declined')}
                  >
                    <X className="h-3 w-3" /> Decline
                  </Button>
                </div>
              </div>
            )}
            {event.htmlLink && (
              <a
                href={event.htmlLink}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
              >
                Open in Google Calendar <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        )}

        <DialogFooter>
          {editing ? (
            <>
              <Button variant="outline" onClick={() => setEditing(false)}>
                Cancel
              </Button>
              <Button onClick={fireSave} disabled={updateMutation.isPending}>
                {updateMutation.isPending ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving
                  </>
                ) : (
                  <>
                    <Save className="h-3.5 w-3.5" /> Save
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={fireDelete}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="h-3.5 w-3.5 text-destructive" /> Delete
              </Button>
              <Button onClick={() => setEditing(true)}>Edit</Button>
            </>
          )}
        </DialogFooter>
        <RecurrenceScopeDialog
          open={pending !== null}
          action={pending}
          pending={updateMutation.isPending || deleteMutation.isPending}
          onCancel={() => setPending(null)}
          onPick={pickScope}
        />
      </DialogContent>
    </Dialog>
  );
}

function RecurrenceScopeDialog({
  open,
  action,
  pending,
  onCancel,
  onPick,
}: {
  open: boolean;
  action: 'save' | 'delete' | null;
  pending: boolean;
  onCancel: () => void;
  onPick: (scope: RecurrenceScope) => void;
}) {
  const verb = action === 'delete' ? 'Delete' : 'Save';
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{verb} recurring event</DialogTitle>
          <DialogDescription>
            This event repeats. Apply your change to which instances?
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Button
            variant="outline"
            disabled={pending}
            onClick={() => onPick('this')}
          >
            This event only
          </Button>
          <Button
            variant="outline"
            disabled={pending}
            onClick={() => onPick('following')}
          >
            This and following events
          </Button>
          <Button
            variant="outline"
            disabled={pending}
            onClick={() => onPick('all')}
          >
            All events in the series
          </Button>
        </div>
        <DialogFooter>
          <Button variant="ghost" disabled={pending} onClick={onCancel}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function composeIso(dateStr: string, timeStr: string, allDay: boolean): string {
  if (allDay) {
    const d = new Date(`${dateStr}T00:00:00`);
    return d.toISOString();
  }
  const d = new Date(`${dateStr}T${timeStr}:00`);
  return d.toISOString();
}

function parseAttendees(raw: string): string[] {
  return raw
    .split(/[,\s;]+/)
    .map((s) => s.trim())
    .filter((s) => /.+@.+\..+/.test(s));
}
